// Full-page review for a single Writing/Speaking submission. Reachable from
// Results > Practice via the "Review" button. Mirrors MockReviewPage's layout
// so the prompt is clearly separated from the student's response — no
// accordion, no dropdown, no ambiguity about what was asked vs what was
// written.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Headphones, Mic, PenLine } from 'lucide-react';

import { api } from '@/lib/api';
import { dedupeGateFlags, gateFlagText, prettyTask, statusMeta } from '@/lib/labels';
import AppShell from '@/components/AppShell';
import ContentAudioPlayer from '@/components/ContentAudioPlayer';
import ContentImage from '@/components/ContentImage';
import DiagramCard from '@/components/DiagramCard';
import SignedAudioPlayer from '@/components/SignedAudioPlayer';
import { cn } from '@/lib/utils';

const SECTION_ICON = { reading: BookOpen, listening: Headphones, writing: PenLine, speaking: Mic };
const when = (iso) => (iso ? `${iso.slice(0, 10)} · ${iso.slice(11, 16)}` : '');

export default function SubmissionReviewPage() {
  const { exam, submission_id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [error, setError] = useState('');
  // Once the 90-second poll gives up we swap the "refreshes automatically"
  // copy for an honest "we couldn't finish marking" message — the spinner
  // has to go the moment the polling stops or we'd be lying about progress.
  const [pollExhausted, setPollExhausted] = useState(false);

  // Live-refresh poll. Backend now grades in the background so a fresh
  // submission lands here as `queued` and flips to `scored` / `gate_zeroed`
  // within ~15–25s. We poll every 1.5s for up to 90s, then stop and let the
  // page render the honest "still working" copy — never a stuck spinner and
  // never a misleading "marking is busy" message.
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    let timer = null;
    const MAX_ATTEMPTS = 60;      // 60 × 1500ms ≈ 90 seconds of active polling
    const INTERVAL_MS = 1500;

    const isTerminal = (s) => s === 'scored' || s === 'gate_zeroed';

    const load = async () => {
      try {
        const res = await api.submissionDetail(exam, submission_id);
        const s = res.data.submission;
        if (cancelled) return;
        setSub(s);
        if (isTerminal(s.status)) return;

        // Nudge the grader once — if the background task never fired
        // (server restart, missed event loop), /score will kick it now.
        // Silent-fail if rescore itself errored; the next poll will retry.
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setPollExhausted(true);
          return;
        }
        try { await api.rescoreSubmission(exam, submission_id); } catch { /* keep polling */ }
        if (cancelled) return;
        timer = setTimeout(load, INTERVAL_MS);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load this submission.');
      }
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [exam, submission_id]);

  // Whether to render the audio player at all. The signed-URL fetch + the
  // refetch-on-error retry live inside <SignedAudioPlayer/>, so a student
  // who reads the feedback for longer than the URL's TTL still gets working
  // playback when they hit play.
  const showAudio = sub && sub.section === 'speaking' && sub.status !== 'gate_zeroed';

  if (error) return (
    <AppShell>
      <button type="button" onClick={() => navigate(`/${exam}/results?view=practice`)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="submission-review-back">
        <ArrowLeft className="h-4 w-4" /> Results
      </button>
      <p className="mono-stat mt-6">{exam} · response review</p>
      <h1 className="h-display mt-2 text-3xl">Couldn’t load this response.</h1>
      <p className="mt-2 text-sm text-slate-500">{error}</p>
    </AppShell>
  );
  if (!sub) return <AppShell><p className="py-10 text-slate-400">Loading…</p></AppShell>;

  const Icon = SECTION_ICON[sub.section] || PenLine;
  const st = statusMeta(sub.status);
  const zeroed = sub.status === 'gate_zeroed';
  const fb = sub.feedback || {};
  const band = !zeroed && (fb.band_estimate || fb.clb_band_estimate);
  const spec = sub.prompt?.response_spec || {};
  const stim = sub.prompt?.stimulus || {};
  const title = sub.prompt?.title || prettyTask(sub.task_type);

  return (
    <AppShell>
      <button type="button" onClick={() => navigate(`/${exam}/results?view=practice&skill=${sub.section}`)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="submission-review-back">
        <ArrowLeft className="h-4 w-4" /> Results
      </button>

      <p className="mono-stat mt-6">{exam} · response review</p>
      <div className="mt-2 flex flex-wrap items-baseline gap-3">
        <h1 className="h-display text-3xl" data-testid="submission-review-title">{title}</h1>
        <span className={cn('rounded px-2 py-0.5 text-xs font-semibold', st.cls)}>{st.text}</span>
      </div>
      <p className="mt-1 flex items-center gap-2 text-sm text-slate-500">
        <Icon className="h-3.5 w-3.5" />
        <span className="capitalize">{sub.section}</span>
        <span>·</span>
        <span>{when(sub.created_at)}</span>
      </p>

      <div className="mt-8 grid max-w-5xl gap-6 lg:grid-cols-5">
        {/* LEFT — the actual task the student was answering */}
        <aside className="lg:col-span-2">
          <div className="border border-slate-200 bg-white" data-testid="submission-review-prompt">
            <div className="border-b border-slate-100 bg-slate-50/70 px-5 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">The task</p>
            </div>
            <div className="space-y-4 px-5 py-5">
              {/* Speaking prompts vary widely in shape: ASQ has a `transcript`
                  (the question that was asked), Repeat Sentence has a
                  `transcript` (the sentence to repeat), Describe Image has a
                  `diagram` or `image_ref`, and CELPIP tasks pair `text`
                  (situation) with `prompt_text`. Render every field that
                  might carry the actual task — the "no longer available"
                  fallback only fires when ALL of them are absent. */}
              {spec.instruction && (
                <p className="text-xs leading-5 text-slate-500">{spec.instruction}</p>
              )}
              {stim.text && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Situation</p>
                  <p className="mt-1 whitespace-pre-wrap text-[15px] leading-7 text-slate-800" data-testid="submission-review-stim-text">{stim.text}</p>
                </div>
              )}
              {stim.transcript && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    {sub.task_type === 'pte_asq' ? 'Question you heard' : sub.task_type === 'pte_repeat_sentence' ? 'Sentence you heard' : 'What you heard'}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-[15px] leading-7 text-slate-800" data-testid="submission-review-stim-transcript">{stim.transcript}</p>
                </div>
              )}
              {stim.diagram && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Diagram</p>
                  <div className="mt-2" data-testid="submission-review-stim-diagram">
                    <DiagramCard diagram={stim.diagram} />
                  </div>
                </div>
              )}
              {stim.image_ref && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Picture</p>
                  <div className="mt-2" data-testid="submission-review-stim-image">
                    <ContentImage imageRef={stim.image_ref} caption={stim.scene} />
                  </div>
                </div>
              )}
              {stim.audio_ref && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Prompt audio</p>
                  <ContentAudioPlayer audioRef={stim.audio_ref} className="mt-2" testId="submission-review-stim-audio" />
                </div>
              )}
              {spec.prompt_text && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Prompt</p>
                  <p className="mt-1 whitespace-pre-wrap text-[15px] leading-7 text-slate-800">{spec.prompt_text}</p>
                </div>
              )}
              {spec.bullets?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Address every bullet</p>
                  <ul className="mt-2 space-y-2">
                    {spec.bullets.map((b, i) => (
                      <li key={i} className="flex gap-2.5 text-sm leading-6 text-slate-700">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-500" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {spec.word_guidance && (
                <p className="text-xs text-slate-500">Target: {spec.word_guidance}</p>
              )}
              {spec.register_note && (
                <p className="border-t border-slate-100 pt-3 text-xs leading-5 text-slate-500">{spec.register_note}</p>
              )}
              {!spec.prompt_text && !stim.text && !stim.transcript && !stim.diagram && !stim.image_ref && !stim.audio_ref && !spec.instruction && (
                <p className="text-sm text-slate-500">Original prompt is no longer available for this task.</p>
              )}
            </div>
          </div>
        </aside>

        {/* RIGHT — the response and the marking */}
        <section className="lg:col-span-3 space-y-6">
          {zeroed ? (
            <div className="border-l-4 border-rose-500 bg-rose-50/60 px-5 py-4" data-testid="submission-review-gate-zeroed">
              <p className="text-xs font-semibold uppercase tracking-wide text-rose-700">Would score 0 on the real exam</p>
              <ul className="mt-2 space-y-1">
                {dedupeGateFlags(sub.gate_flags || ['empty']).map((flag, i) => (
                  <li key={i} className="text-sm leading-6 text-slate-700">{gateFlagText(flag)}</li>
                ))}
              </ul>
            </div>
          ) : (
            <>
              {band && (
                <div className="border border-slate-200 bg-white px-5 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Band estimate</p>
                  <p className="h-display mt-1 text-4xl text-slate-900" data-testid="submission-review-band">{band}</p>
                </div>
              )}
              {fb.feature_tags?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">What was found</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {fb.feature_tags.map((tag) => (
                      <span key={tag} className="rounded bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
              {fb.revisions?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Concrete revisions</p>
                  <ul className="mt-2 space-y-2">
                    {fb.revisions.map((rev, i) => (
                      <li key={i} className="border-l-2 border-indigo-600 pl-3 text-sm leading-6 text-slate-700">{rev}</li>
                    ))}
                  </ul>
                </div>
              )}
              {sub.deterministic_estimate && (
                <p className="text-sm text-slate-600">
                  Content match: {sub.deterministic_estimate.matched_words} of {sub.deterministic_estimate.target_words} target
                  words in order ({Math.round(sub.deterministic_estimate.content_ratio * 100)}%).
                </p>
              )}
              {sub.text && (
                <div className="border border-slate-200 bg-white">
                  <div className="border-b border-slate-100 bg-slate-50/70 px-5 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Your response · {sub.word_count} words
                    </p>
                  </div>
                  <p className="whitespace-pre-wrap px-5 py-4 text-[15px] leading-7 text-slate-800" data-testid="submission-review-response">{sub.text}</p>
                </div>
              )}
              {sub.transcript && (
                <div className="border border-slate-200 bg-white">
                  <div className="border-b border-slate-100 bg-slate-50/70 px-5 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">What we heard</p>
                  </div>
                  <p className="px-5 py-4 text-[15px] leading-7 text-slate-800" data-testid="submission-review-transcript">{sub.transcript}</p>
                </div>
              )}
              {showAudio && (
                <div className="border border-slate-200 bg-white px-5 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Your recording</p>
                  <SignedAudioPlayer
                    exam={exam}
                    submissionId={submission_id}
                    className="mt-2 w-full"
                    testId="submission-review-audio"
                  />
                </div>
              )}
              {/* Pending block is gated on the SUBMISSION STATUS, not on the
                  absence of a band. That way an already-scored ASQ (feedback
                  has no `band`) doesn't wear the spinner forever. Once the
                  90-second poll gives up we drop the spinner and swap in an
                  honest "still working" line — no more silently-frozen UI. */}
              {sub.status !== 'scored' && sub.status !== 'gate_zeroed' && !pollExhausted && (
                <div className="flex items-center gap-3 border border-slate-200 bg-slate-50 px-5 py-4" data-testid="submission-review-pending">
                  <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
                  <p className="text-sm leading-6 text-slate-700">
                    Marking your response — CLB feedback usually lands within about 30 seconds. This page refreshes automatically.
                  </p>
                </div>
              )}
              {sub.status !== 'scored' && sub.status !== 'gate_zeroed' && pollExhausted && (
                <div className="border-l-2 border-amber-500 bg-amber-50/60 px-5 py-4" data-testid="submission-review-pending-exhausted">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Still marking</p>
                  <p className="mt-1 text-sm leading-6 text-slate-700">
                    We couldn’t finish marking this in the usual window. Your response is safe — reload this page in a minute
                    and the feedback should be here.
                  </p>
                </div>
              )}
            </>
          )}

          <div className="pt-2">
            <Link
              to={`/${exam}/results?view=practice&skill=${sub.section}`}
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
              data-testid="submission-review-back-bottom"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Results
            </Link>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
